/* RN WebView 양방향 통신 브릿지 — PlHouse */

let _webReadySent = false;

/** RN WebView 내부에서 실행 중인지 확인 */
export function isInRnWebView() {
  return !!(window.ReactNativeWebView);
}

/** Web → RN 메시지 전송 */
export function postToRN(type, payload = {}) {
  if (!isInRnWebView()) return false;
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, payload }));
    return true;
  } catch {
    return false;
  }
}

/** RN → Web 메시지 수신 리스너 등록 (cleanup 함수 반환) */
export function listenWebviewMessages(handler) {
  const listener = (e) => {
    try {
      const msg = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      if (msg.payload && typeof msg.payload === "object") {
        handler({ ...msg.payload, type: msg.type });
      } else {
        handler(msg);
      }
    } catch {
      // ignore non-JSON
    }
  };
  window.addEventListener("message", listener);
  document.addEventListener("message", listener);
  return () => {
    window.removeEventListener("message", listener);
    document.removeEventListener("message", listener);
  };
}

/** 웹 준비 완료 신호 (1회만) */
export function sendWebReadyOnce() {
  if (_webReadySent) return;
  _webReadySent = true;
  postToRN("WEB_READY");
}

/** 특정 타입 메시지 대기 (Promise) */
export function waitForRnMessage(type, timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    const off = listenWebviewMessages((msg) => {
      if (msg.type === type) { off(); resolve(msg); }
    });
    setTimeout(() => { off(); reject(new Error(`Timeout waiting for ${type}`)); }, timeoutMs);
  });
}

/** 네비게이션 상태 전송 */
export function sendNavState(navState) {
  postToRN("NAV_STATE", navState);
}

/** RN에게 FCM 토큰 요청 */
export function requestPushToken() {
  return postToRN("GET_PUSH_TOKEN");
}

/** PUSH_TOKEN 메시지 수신 리스너 (token, platform 콜백) */
export function onPushToken(callback) {
  return listenWebviewMessages((msg) => {
    if (msg.type === "PUSH_TOKEN" && msg.token) {
      callback(msg.token, msg.platform || "android");
    }
    // FCM_TOKEN (legacy) 도 처리
    if (msg.type === "FCM_TOKEN" && msg.token) {
      callback(msg.token, "android");
    }
  });
}

/** PUSH_EVENT 수신 리스너 (포그라운드 푸시 도착) */
export function onPushEvent(callback) {
  return listenWebviewMessages((msg) => {
    if (msg.type === "PUSH_EVENT") {
      callback(msg);
    }
  });
}

/** 권한 요청 */
export function requestPermission() {
  postToRN("REQUEST_PERMISSION");
}

/** 기존 권한 상태 확인 */
export function checkInitialPermission() {
  postToRN("CHECK_INITIAL_PERMISSION");
}

/** 앱 설정 열기 */
export function openSettings() {
  postToRN("OPEN_SETTINGS");
}

/** 앱 종료 (Android) */
export function sendExitApp() {
  postToRN("EXIT_APP");
}

/**
 * 외부 링크를 RN 에게 위임해서 연다 (raw `open::URL` 프로토콜).
 * RN WebView 안에서 window.location.href 로 외부 URL(스토어/지도앱 등)을
 * 직접 열면 WebView 가 그 페이지로 빠져 앱 복귀가 안 되는 문제 방지.
 * RN 은 이 메시지를 받아 Linking.openURL 로 외부(브라우저/앱)에서 연다.
 */
export function openExternalViaRN(url) {
  if (!isInRnWebView() || !url) return false;
  try {
    window.ReactNativeWebView.postMessage("open::" + encodeURI(String(url)));
    return true;
  } catch {
    return false;
  }
}

/** 디버그 로그 (sessionStorage 순환 버퍼) */
export function appendRnLog(type, keys, tokenHint) {
  try {
    const prev = JSON.parse(sessionStorage.getItem("__rnLogs") || "[]");
    prev.push({ type, keys: keys || [], t: new Date().toLocaleTimeString(), tokenHint: tokenHint || "" });
    if (prev.length > 20) prev.shift();
    sessionStorage.setItem("__rnLogs", JSON.stringify(prev));
  } catch {}
}
